<template>
  <div class="telly-shell">
    <!-- Header -->
    <header class="telly-header">
      <div>
        <div class="eyebrow">Reconciliation</div>
        <h1>Telly</h1>
        <p class="subtitle">Cross-check approved orders against bank statements (GPay / Paytm / PDF). Pure read — no orders are approved or modified.</p>
      </div>
    </header>

    <!-- ==================== Config: level + scope + date ==================== -->
    <section class="config-grid">
      <!-- Level + scope -->
      <div class="telly-card">
        <div class="card-head">
          <div class="card-title">Reconciliation Level</div>
          <div class="card-sub">Pick a branch (covers every bank under it) or a single bank.</div>
        </div>
        <div class="chip-group chip-group-lg">
          <button
            type="button"
            class="chip"
            :class="{ 'chip-active': mode === 'branch' }"
            @click="mode = 'branch'"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="chip-icon">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M9 17h1M14 9h1M14 13h1M14 17h1" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Branch level
          </button>
          <button
            type="button"
            class="chip"
            :class="{ 'chip-active': mode === 'bank' }"
            @click="mode = 'bank'"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="chip-icon">
              <path d="M3 10h18M5 10V6l7-3 7 3v4M6 10v9M18 10v9M10 10v9M14 10v9M3 21h18" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Bank level
          </button>
        </div>

        <div v-if="mode === 'bank'" class="scope-field">
          <label>Bank <span class="label-hint">· {{ banks.length }} total (inactive + retired included)</span></label>
          <select v-model="bankUsername" class="telly-input">
            <option value="">— select bank —</option>
            <option v-for="b in banks" :key="b.username" :value="b.username">
              {{ b.name || b.username }} ({{ b.username }}){{ b._statusLabel ? ` — ${b._statusLabel}` : '' }}
            </option>
          </select>
        </div>
        <div v-if="mode === 'branch' && needsBranchSelection" class="scope-field">
          <label>Branch <span class="label-hint">· {{ branches.length }} total (inactive + retired included)</span></label>
          <select v-model="branchId" class="telly-input">
            <option value="">— select branch —</option>
            <option v-for="br in branches" :key="br.id" :value="br.id">
              {{ br.name || br.username }} ({{ br.username }}){{ br._statusLabel ? ` — ${br._statusLabel}` : '' }}
            </option>
          </select>
          <p class="hint">Reconciliation covers every bank under this branch, including retired ones with historical orders.</p>
        </div>
      </div>

      <!-- Date range -->
      <div class="telly-card">
        <div class="card-head">
          <div class="card-title">Date Range (IST)</div>
          <div class="card-sub">Filters approved orders by their approval timestamp.</div>
        </div>
        <div class="date-row">
          <label class="scope-field flex-1">
            <span>From</span>
            <input v-model="dateFrom" type="date" class="telly-input" />
          </label>
          <span class="date-arrow">→</span>
          <label class="scope-field flex-1">
            <span>To</span>
            <input v-model="dateTo" type="date" class="telly-input" />
          </label>
        </div>
      </div>
    </section>

    <!-- ==================== File uploader ==================== -->
    <section class="telly-card">
      <div class="card-head">
        <div class="card-title">Upload Statements
          <span v-if="files.length" class="head-count">{{ files.length }} selected</span>
        </div>
        <div class="card-sub">GPay / Paytm / bank-PDF auto-detected per file.</div>
      </div>

      <div
        class="dropzone"
        :class="{ 'dropzone-has-files': files.length > 0 }"
        @dragover.prevent="dragActive = true"
        @dragleave.prevent="dragActive = false"
        @drop.prevent="onDrop"
        :data-drag-active="dragActive"
        @click="$refs.fp.click()"
      >
        <input ref="fp" type="file" multiple accept=".csv,.pdf" @change="onPick" class="hidden-input" />
        <div class="dropzone-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M12 3v14M6 9l6-6 6 6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M4 21h16" stroke-linecap="round" />
          </svg>
        </div>
        <div class="dropzone-title">Drop CSV or PDF files here</div>
        <div class="dropzone-sub">or <span class="dropzone-cta">click to choose</span></div>
      </div>

      <ul v-if="files.length" class="file-list">
        <li v-for="(f, i) in files" :key="i" class="file-item">
          <div class="file-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke-linejoin="round" />
              <path d="M14 2v6h6" stroke-linejoin="round" />
            </svg>
          </div>
          <span class="file-name">{{ f.name }}</span>
          <span class="file-size">{{ Math.round(f.size / 1024) }} KB</span>
          <button
            type="button"
            class="file-remove"
            @click.stop="files.splice(i, 1)"
            title="Remove file"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
            </svg>
          </button>
        </li>
      </ul>
    </section>

    <!-- ==================== Run + error ==================== -->
    <div class="run-row" v-if="can('settlement.reconcile') || can('settlement.cross_validate')">
      <button
        type="button"
        class="btn btn-primary btn-lg"
        :disabled="!canRun || running"
        @click="run"
      >
        <span v-if="running" class="btn-spinner"></span>
        <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="btn-icon">
          <path d="M4 4v6h6M20 20v-6h-6" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M4 10a8 8 0 0113.66-4.36L20 8M20 14a8 8 0 01-13.66 4.36L4 16" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ running ? 'Reconciling…' : 'Reconcile' }}
      </button>
    </div>

    <transition name="fade">
      <div v-if="error" class="callout callout-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
        </svg>
        <span>{{ error }}</span>
      </div>
    </transition>

    <!-- ==================== Report ==================== -->
    <template v-if="report">
      <!-- Files parsed -->
      <section class="telly-card">
        <div class="card-head">
          <div class="card-title">
            Files Parsed
            <span class="head-count">{{ report.fileSummary.length }}</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="telly-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Detected</th>
                <th class="th-num">Total rows</th>
                <th class="th-num">Parsed</th>
                <th class="th-num">Skipped</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(f, i) in report.fileSummary" :key="i">
                <td>{{ f.fileName }}</td>
                <td>
                  <span class="pill" :class="'pill-' + f.source">{{ f.source }}</span>
                  <span v-if="f.error" class="text-danger-inline">{{ f.error }}</span>
                </td>
                <td class="td-num">{{ f.rowCount }}</td>
                <td class="td-num">{{ f.parsedRows != null ? f.parsedRows : f.rowCount }}</td>
                <td class="td-num" :class="(f.skippedCount || 0) > 0 ? 'text-orange' : 'text-muted'">
                  {{ f.skippedCount || 0 }}
                  <div v-if="f.skippedReasons && Object.keys(f.skippedReasons).length" class="td-sub">
                    {{ summariseReasons(f.skippedReasons) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="totalSkipped > 0" class="callout callout-warn callout-inline">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 9v4M12 17h.01" stroke-linecap="round" />
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>{{ totalSkipped }} row(s) were skipped during parsing — see the <b>Skipped statement rows</b> section below for details.</span>
        </div>
      </section>

      <!--
        Summary stat cards — clickable. Click opens the matching bucket
        below (if collapsed) and smooth-scrolls to it. Empty buckets stay
        non-clickable so we don't scroll to nowhere.
      -->
      <section class="stat-grid">
        <button
          type="button"
          class="stat-card stat-matched"
          :class="{ 'stat-card-disabled': !report.matched.length }"
          :disabled="!report.matched.length"
          @click="jumpToBucket('matched')"
          title="Show matched orders"
        >
          <div class="stat-label">Matched</div>
          <div class="stat-value">{{ report.totals.matchedCount }}</div>
          <div class="stat-sub">₹{{ formatNum(report.totals.matchedAmount) }}</div>
        </button>
        <button
          type="button"
          class="stat-card stat-mismatch"
          :class="{ 'stat-card-disabled': !report.amountMismatch.length }"
          :disabled="!report.amountMismatch.length"
          @click="jumpToBucket('amountMismatch')"
          title="Show amount mismatch rows"
        >
          <div class="stat-label">Amount Mismatch</div>
          <div class="stat-value">{{ report.totals.amountMismatchCount }}</div>
          <div class="stat-sub">order ₹{{ formatNum(report.totals.amountMismatchAmount) }}</div>
        </button>
        <button
          type="button"
          class="stat-card stat-missing"
          :class="{ 'stat-card-disabled': !report.missingFromStatements.length }"
          :disabled="!report.missingFromStatements.length"
          @click="jumpToBucket('missingFromStatements')"
          title="Show missing-in-statement orders"
        >
          <div class="stat-label">Missing in Statement</div>
          <div class="stat-value">{{ report.totals.missingFromStatementsCount }}</div>
          <div class="stat-sub">₹{{ formatNum(report.totals.missingFromStatementsAmount) }}</div>
        </button>
        <button
          type="button"
          class="stat-card stat-orphan"
          :class="{ 'stat-card-disabled': !report.orphanStatementRows.length }"
          :disabled="!report.orphanStatementRows.length"
          @click="jumpToBucket('orphanStatementRows')"
          title="Show orphan statement rows"
        >
          <div class="stat-label">Orphan Statement Rows</div>
          <div class="stat-value">{{ report.totals.orphanStatementCount }}</div>
          <div class="stat-sub">₹{{ formatNum(report.totals.orphanStatementAmount) }}</div>
        </button>
      </section>

      <!-- Per-bank breakdown -->
      <section v-if="report.perBank && report.perBank.length > 1" class="telly-card">
        <div class="card-head">
          <div class="card-title">Per-Bank Breakdown</div>
        </div>
        <div class="table-wrap">
          <table class="telly-table">
            <thead>
              <tr>
                <th>Bank</th>
                <th class="th-num">Approved ₹</th>
                <th class="th-num">Matched ₹</th>
                <th class="th-num">Diff</th>
                <th class="th-num">Mismatch</th>
                <th class="th-num">Missing</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="b in report.perBank" :key="b.bankUsername">
                <td class="td-entity">{{ b.bankUsername }}</td>
                <td class="td-num">{{ formatNum(b.approvedAmount) }}</td>
                <td class="td-num">{{ formatNum(b.matchedAmount) }}</td>
                <td class="td-num td-strong" :class="diffColor(b.matchedAmount - b.approvedAmount)">
                  {{ signed(b.matchedAmount - b.approvedAmount) }}
                </td>
                <td class="td-num">{{ b.amountMismatchCount }}</td>
                <td class="td-num">{{ b.missingFromStatementsCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Bucket: matched -->
      <details v-if="report.matched.length" ref="bucketMatched" class="bucket bucket-success">
        <summary class="bucket-summary">
          <span class="bucket-dot"></span>
          <span class="bucket-title">Matched orders</span>
          <span class="bucket-count">{{ report.matched.length }}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </summary>
        <div class="bucket-body">
          <div class="table-wrap">
            <table class="telly-table">
              <thead>
                <tr>
                  <th>Receipt</th><th>Bank</th><th>UTR</th>
                  <th class="th-num">Amount</th><th>Source</th><th>Approval</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in report.matched" :key="m.refID">
                  <td>{{ m.receiptId || m.merchantOrderId }}</td>
                  <td>{{ m.bankUsername }}</td>
                  <td class="td-mono">{{ m.transactionID }}</td>
                  <td class="td-num td-strong">₹{{ formatNum(m.amount) }}</td>
                  <td><span class="pill" :class="'pill-' + m.statementSource">{{ m.statementSource }}</span></td>
                  <td class="td-mini">{{ m.approvalMethod || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" class="csv-btn" @click="exportCsv('matched')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="csv-icon">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Export CSV
          </button>
        </div>
      </details>

      <!-- Bucket: amount mismatch -->
      <details v-if="report.amountMismatch.length" ref="bucketAmountMismatch" class="bucket bucket-warn" open>
        <summary class="bucket-summary">
          <span class="bucket-dot"></span>
          <span class="bucket-title">Amount mismatch</span>
          <span class="bucket-count">{{ report.amountMismatch.length }}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </summary>
        <div class="bucket-body">
          <div class="table-wrap">
            <table class="telly-table">
              <thead>
                <tr>
                  <th>Receipt</th><th>Bank</th><th>UTR</th>
                  <th class="th-num">Order ₹</th><th class="th-num">Statement ₹</th><th class="th-num">Diff</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in report.amountMismatch" :key="m.refID">
                  <td>{{ m.receiptId || m.merchantOrderId }}</td>
                  <td>{{ m.bankUsername }}</td>
                  <td class="td-mono">{{ m.transactionID }}</td>
                  <td class="td-num">₹{{ formatNum(m.amount) }}</td>
                  <td class="td-num">₹{{ formatNum(m.statementAmount) }}</td>
                  <td class="td-num td-strong" :class="diffColor(m.diff)">{{ signed(m.diff) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" class="csv-btn" @click="exportCsv('amountMismatch')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="csv-icon">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Export CSV
          </button>
        </div>
      </details>

      <!-- Bucket: missing -->
      <details v-if="report.missingFromStatements.length" ref="bucketMissingFromStatements" class="bucket bucket-danger" open>
        <summary class="bucket-summary">
          <span class="bucket-dot"></span>
          <span class="bucket-title">Missing in statements</span>
          <span class="bucket-count">{{ report.missingFromStatements.length }}</span>
          <span class="bucket-sub">approved orders whose UTR isn't in any uploaded file</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </summary>
        <div class="bucket-body">
          <div class="table-wrap">
            <table class="telly-table">
              <thead>
                <tr>
                  <th>Receipt</th><th>Bank</th><th>UTR</th>
                  <th class="th-num">Amount</th><th>Approval</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in report.missingFromStatements" :key="m.refID">
                  <td>{{ m.receiptId || m.merchantOrderId }}</td>
                  <td>{{ m.bankUsername }}</td>
                  <td class="td-mono">{{ m.transactionID }}</td>
                  <td class="td-num td-strong">₹{{ formatNum(m.amount) }}</td>
                  <td class="td-mini">{{ m.approvalMethod || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" class="csv-btn" @click="exportCsv('missingFromStatements')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="csv-icon">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Export CSV
          </button>
        </div>
      </details>

      <!-- Bucket: skipped statement rows -->
      <details v-if="(report.skippedStatementRows || []).length" class="bucket bucket-warn" open>
        <summary class="bucket-summary">
          <span class="bucket-dot"></span>
          <span class="bucket-title">Skipped statement rows</span>
          <span class="bucket-count">{{ report.skippedStatementRows.length }}</span>
          <span class="bucket-sub">UTR or amount could not be detected — review your file format</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </summary>
        <div class="bucket-body">
          <div class="table-wrap">
            <table class="telly-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th class="th-num">Row #</th>
                  <th>Reason</th>
                  <th>Message</th>
                  <th>UTR (if any)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in report.skippedStatementRows" :key="i">
                  <td class="td-clip">{{ s.fileName }}</td>
                  <td class="td-num">{{ s.rowNumber }}</td>
                  <td><span class="pill" :class="'pill-reason-' + s.reason">{{ s.reason }}</span></td>
                  <td class="td-mini">{{ s.message }}</td>
                  <td class="td-mono">{{ s.utr || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" class="csv-btn" @click="exportCsv('skippedStatementRows')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="csv-icon">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Export CSV
          </button>
        </div>
      </details>

      <!-- Bucket: orphans -->
      <details v-if="report.orphanStatementRows.length" ref="bucketOrphanStatementRows" class="bucket bucket-neutral">
        <summary class="bucket-summary">
          <span class="bucket-dot"></span>
          <span class="bucket-title">Orphan statement rows</span>
          <span class="bucket-count">{{ report.orphanStatementRows.length }}</span>
          <span class="bucket-sub">UTRs in statements but no matching approved order in scope</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="bucket-chevron">
            <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </summary>
        <div class="bucket-body">
          <div class="table-wrap">
            <table class="telly-table">
              <thead>
                <tr>
                  <th>UTR</th>
                  <th class="th-num">Amount</th>
                  <th>Date</th>
                  <th>Payer</th>
                  <th>Source</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(o, i) in report.orphanStatementRows" :key="i">
                  <td class="td-mono">{{ o.utr }}</td>
                  <td class="td-num td-strong">₹{{ formatNum(o.amount) }}</td>
                  <td class="td-mini">{{ o.txnDate || '—' }}</td>
                  <td class="td-mini">{{ o.payerName || '—' }}</td>
                  <td><span class="pill" :class="'pill-' + o.source">{{ o.source }}</span></td>
                  <td class="td-mini text-muted">
                    <span v-if="o.outOfRange">Out of date range</span>
                    <span v-else-if="!o.success">Statement marked unsuccessful</span>
                    <span v-else>—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" class="csv-btn" @click="exportCsv('orphanStatementRows')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="csv-icon">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Export CSV
          </button>
        </div>
      </details>
    </template>
  </div>
</template>

<script>
import http from '@/http-common.js';

export default {
  name: 'SettlementReconciliation',
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      mode: 'branch',
      bankUsername: '',
      branchId: '',
      dateFrom: today,
      dateTo: today,
      banks: [],
      branches: [],
      files: [],
      running: false,
      report: null,
      error: null,
      dragActive: false,
    };
  },
  computed: {
    role() { return this.$store.getters.role; },
    // A subadmin's branch IS themselves — no picker needed. For admin /
    // superadmin we surface a dropdown so they pick a branch (subadmin id)
    // before reconciling.
    needsBranchSelection() {
      return ['admin', 'superadmin'].includes(this.role);
    },
    totalSkipped() {
      return (this.report?.skippedStatementRows || []).length;
    },
    canRun() {
      if (!this.dateFrom || !this.dateTo) return false;
      if (!this.files.length) return false;
      if (this.mode === 'bank' && !this.bankUsername) return false;
      if (this.mode === 'branch' && this.needsBranchSelection && !this.branchId) return false;
      return true;
    },
  },
  mounted() {
    this.fetchBanks();
    if (this.needsBranchSelection) this.fetchBranches();
  },
  methods: {
    can(perm) { return this.$store.getters.hasPermission(perm); },
    /*
      Jumps to the detail bucket matching a summary stat card. Opens the
      collapsed <details> element (so the user lands on rows, not a closed
      chevron) and smooth-scrolls it into view. Keyed by bucket name — same
      keys as `exportCsv` / the `report.*` arrays — so future buckets only
      need a `ref="bucket<Key>"` on the <details> to be reachable.
    */
    jumpToBucket(key) {
      const refName = 'bucket' + key.charAt(0).toUpperCase() + key.slice(1);
      const el = this.$refs[refName];
      if (!el) return;
      // Force-open before scrolling — otherwise closed buckets scroll to a
      // 1-line summary that doesn't visually confirm the jump.
      if (!el.open) el.open = true;
      // Wait a tick so the browser lays out the newly opened body before
      // computing scroll offsets; otherwise the target rect is still the
      // closed height and we under-scroll.
      this.$nextTick(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    async fetchBanks() {
      // `status=any` = include inactive + soft-deleted banks. Their
      // historical orders are still reconcilable, so the picker must
      // surface them even when they're no longer active.
      try {
        const r = await http.get('/users/user?status=any');
        if (r.status === 200) this.banks = this.sortAndDecorate(r.data || []);
      } catch (e) { /* swallow — banks list is only required in bank mode */ }
    },
    async fetchBranches() {
      try {
        const r = await http.get('/users/subadmin?status=any');
        if (r.status === 200) this.branches = this.sortAndDecorate(r.data || []);
      } catch (e) { /* swallow — branches list only relevant for admin+ in branch mode */ }
    },
    // Sort active-first, then by display name; and attach a small human-
    // readable status label the template can render as a suffix.
    sortAndDecorate(list) {
      return [...list]
        .map((u) => {
          const isDeleted = Number(u.is_deleted) === 1;
          const isActive = Number(u.status) === 1;
          let statusLabel = "";
          if (isDeleted) statusLabel = "Deleted";
          else if (!isActive) statusLabel = "Inactive";
          return { ...u, _statusLabel: statusLabel };
        })
        .sort((a, b) => {
          // Active (no label) first, then inactive, then deleted.
          const rank = (u) => (Number(u.is_deleted) === 1 ? 2 : Number(u.status) !== 1 ? 1 : 0);
          const ra = rank(a);
          const rb = rank(b);
          if (ra !== rb) return ra - rb;
          const an = (a.name || a.username || "").toLowerCase();
          const bn = (b.name || b.username || "").toLowerCase();
          return an.localeCompare(bn);
        });
    },
    onPick(e) {
      this.acceptFiles(Array.from(e.target.files || []));
      e.target.value = '';
    },
    onDrop(e) {
      this.dragActive = false;
      this.acceptFiles(Array.from(e.dataTransfer.files || []));
    },
    acceptFiles(list) {
      const valid = list.filter((f) => /\.(csv|pdf)$/i.test(f.name));
      this.files.push(...valid);
    },
    async run() {
      this.running = true;
      this.error = null;
      this.report = null;
      try {
        const fd = new FormData();
        fd.append('dateFrom', this.dateFrom);
        fd.append('dateTo', this.dateTo);
        if (this.mode === 'bank') fd.append('bankUsername', this.bankUsername);
        if (this.mode === 'branch' && this.needsBranchSelection && this.branchId) {
          fd.append('branchId', this.branchId);
        }
        for (const f of this.files) fd.append('statements', f);

        const r = await http.post('/orders/settlement-reconcile', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (r.status === 200 && r.data && r.data.success) {
          this.report = r.data;
        } else {
          this.error = r.data?.message || 'Reconciliation failed.';
        }
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Reconciliation failed.';
      } finally {
        this.running = false;
      }
    },
    exportCsv(bucket) {
      const rows = this.report[bucket] || [];
      if (!rows.length) return;
      const cols = Object.keys(rows[0]);
      const header = cols.join(',');
      const body = rows.map((r) =>
        cols.map((c) => {
          const v = r[c] == null ? '' : String(r[c]).replace(/"/g, '""');
          return /[",\n]/.test(v) ? `"${v}"` : v;
        }).join(',')
      ).join('\n');
      const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reconcile_${bucket}_${this.dateFrom}_${this.dateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    formatNum(n) {
      const v = Number(n) || 0;
      return v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    },
    signed(n) {
      const v = Number(n) || 0;
      const formatted = Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
      return (v > 0 ? '+₹' : v < 0 ? '−₹' : '₹') + formatted;
    },
    diffColor(n) {
      if (n > 0.01) return 'text-orange';
      if (n < -0.01) return 'text-danger';
      return 'text-success';
    },
    summariseReasons(reasonMap) {
      // { missing_utr: 3, missing_amount: 1 }  →  "3 missing UTR, 1 missing amount"
      const labels = { missing_utr: 'missing UTR', missing_amount: 'missing amount' };
      return Object.entries(reasonMap || {})
        .map(([k, v]) => `${v} ${labels[k] || k}`)
        .join(', ');
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.telly-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.telly-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.telly-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
}

/* ==================== Card ==================== */
.telly-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.card-head {}
.card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.card-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}
.head-count {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 999px;
}

/* Config grid */
.config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 900px) { .config-grid { grid-template-columns: 1fr; } }

/* ==================== Chips ==================== */
.chip-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.12s;
}
/* Hover only when NOT active — otherwise .chip:hover (higher specificity
   than .chip-active) would paint the near-white hover bg over the gradient
   and leave the white text unreadable. */
.chip:not(.chip-active):hover { border-color: #cbd5e1; background: #f8fafc; }
.chip-active {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 12px -4px rgba(27, 42, 71, 0.45);
}
.chip-active:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px -6px rgba(27, 42, 71, 0.55);
}
.chip-icon { width: 15px; height: 15px; }

/* ==================== Fields / inputs ==================== */
.scope-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.scope-field label,
.scope-field > span {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}
.label-hint {
  color: #94a3b8;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  font-size: 10px;
  margin-left: 4px;
}
.telly-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  color: #0f172a;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
}
.telly-input::placeholder { color: #94a3b8; }
.telly-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
select.telly-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

.date-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}
.date-row .flex-1 { flex: 1; }
.date-arrow {
  padding-bottom: 10px;
  color: #cbd5e1;
  font-size: 16px;
}

/* ==================== Dropzone ==================== */
.dropzone {
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  padding: 32px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.dropzone:hover, .dropzone[data-drag-active="true"] {
  border-color: #1B2A47;
  background: #eef2f7;
}
.dropzone-has-files {
  padding: 22px 20px;
}
.hidden-input { display: none; }
.dropzone-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: #eef2f7;
  color: #1B2A47;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.dropzone-icon svg { width: 22px; height: 22px; }
.dropzone-title {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}
.dropzone-sub {
  font-size: 12px;
  color: #64748b;
}
.dropzone-cta {
  color: #1B2A47;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* File list */
.file-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.file-item {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 10px;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
}
.file-icon {
  width: 28px; height: 28px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}
.file-icon svg { width: 14px; height: 14px; }
.file-name {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-size {
  font-size: 11px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.file-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #94a3b8;
  width: 24px; height: 24px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.file-remove:hover { background: #fee2e2; color: #dc2626; }
.file-remove svg { width: 14px; height: 14px; }

/* ==================== Run button ==================== */
.run-row { display: flex; }
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-lg {
  padding: 14px 24px;
  font-size: 14px;
  flex: 1;
  justify-content: center;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 16px; height: 16px; }
.btn-spinner {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ==================== Callouts ==================== */
.callout {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  border: 1px solid transparent;
}
.callout svg { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; }
.callout-error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}
.callout-warn {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}
.callout-inline { margin-top: 4px; }

/* ==================== Stat grid ==================== */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}
.stat-card {
  border-radius: 14px;
  padding: 14px 16px;
  border: 1px solid;
  border-left-width: 4px;
  background: #fff;
  /* Reset native <button> styles so the layout matches the old <div>
     card: left-aligned text, native cursor, no default browser border. */
  font-family: inherit;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.stat-card:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px -12px rgba(15, 23, 42, 0.18);
}
.stat-card:focus-visible {
  outline: 2px solid #1B2A47;
  outline-offset: 2px;
}
.stat-card-disabled,
.stat-card:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.stat-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}
.stat-value {
  font-size: 26px;
  font-weight: 700;
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  line-height: 1.1;
}
.stat-sub {
  font-size: 12px;
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
}
.stat-matched  { border-color: #a7f3d0; background: #ecfdf5; }
.stat-matched  .stat-label { color: #047857; }
.stat-matched  .stat-sub   { color: #059669; }
.stat-mismatch { border-color: #fed7aa; background: #fff7ed; }
.stat-mismatch .stat-label { color: #c2410c; }
.stat-mismatch .stat-sub   { color: #ea580c; }
.stat-missing  { border-color: #fecaca; background: #fef2f2; }
.stat-missing  .stat-label { color: #b91c1c; }
.stat-missing  .stat-sub   { color: #dc2626; }
.stat-orphan   { border-color: #cbd5e1; background: #f8fafc; }
.stat-orphan   .stat-label { color: #475569; }
.stat-orphan   .stat-sub   { color: #64748b; }

/* ==================== Table ==================== */
.table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
}
.telly-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.telly-table thead {
  background: #f8fafc;
}
.telly-table th {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.th-num { text-align: right; }
.telly-table tbody tr {
  transition: background 0.12s;
}
.telly-table tbody tr:nth-child(even) { background: #fafbfc; }
.telly-table tbody tr:hover { background: #eef2f7; }
.telly-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #0f172a;
  vertical-align: top;
}
.telly-table tbody tr:last-child td { border-bottom: none; }
.td-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.td-strong { font-weight: 700; }
.td-mono {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 11px;
  color: #475569;
}
.td-mini { font-size: 11px; color: #64748b; }
.td-clip { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-entity { font-weight: 600; }
.td-sub {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
  font-weight: normal;
}

/* Text/util colors */
.text-danger  { color: #dc2626 !important; }
.text-orange  { color: #ea580c !important; }
.text-success { color: #059669 !important; }
.text-muted   { color: #94a3b8; }
.text-danger-inline {
  color: #b91c1c;
  font-size: 11px;
  margin-left: 6px;
}

/* Pills (badges) */
.pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.pill-gpay   { background: #d1fae5; color: #065f46; }
.pill-paytm  { background: #dbeafe; color: #1e40af; }
.pill-pdf    { background: #eef2f7; color: #6d28d9; }
.pill-unrecognized { background: #fee2e2; color: #b91c1c; }
.pill-reason-missing_utr    { background: #fee2e2; color: #b91c1c; }
.pill-reason-missing_amount { background: #fed7aa; color: #c2410c; }

/* ==================== Bucket (details/summary) ==================== */
.bucket {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left-width: 4px;
  border-radius: 14px;
  overflow: hidden;
  /* .telly-shell is display:flex/column; without this the <details>
     elements were getting flex-shrunk and rendered as a 1-line color strip
     when there was enough content above to fill the viewport. */
  flex-shrink: 0;
}
.bucket-success { border-left-color: #10b981; }
.bucket-warn    { border-left-color: #f59e0b; }
.bucket-danger  { border-left-color: #ef4444; }
.bucket-neutral { border-left-color: #94a3b8; }

.bucket-summary {
  cursor: pointer;
  /* IMPORTANT: <summary> defaults to display:list-item. Setting
     display:flex directly on <summary> worked in most browsers but
     rendered as zero-height in some Chromium/Safari versions once list-
     style was removed. Setting display:list-item + list-style:none
     preserves the element intrinsically, then a flex wrapper inside would
     be cleaner — but keeping flex on summary + a min-height fallback is a
     smaller change and covers every browser we support. */
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  min-height: 44px;
  font-weight: 600;
  color: #0f172a;
  font-size: 14px;
  list-style: none;
  user-select: none;
  transition: background 0.12s;
}
.bucket-summary::-webkit-details-marker { display: none; }
.bucket-summary::marker { content: ""; }
.bucket-summary:hover { background: #f8fafc; }
.bucket-dot {
  width: 8px; height: 8px;
  border-radius: 999px;
  display: inline-block;
}
.bucket-success .bucket-dot { background: #10b981; }
.bucket-warn    .bucket-dot { background: #f59e0b; }
.bucket-danger  .bucket-dot { background: #ef4444; }
.bucket-neutral .bucket-dot { background: #94a3b8; }
.bucket-title { font-weight: 700; }
.bucket-count {
  background: #f1f5f9;
  color: #475569;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}
.bucket-sub {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  margin-left: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bucket-chevron {
  width: 14px; height: 14px;
  color: #94a3b8;
  margin-left: auto;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.bucket[open] .bucket-chevron { transform: rotate(90deg); }
.bucket-body {
  padding: 0 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* CSV export button */
.csv-btn {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid #a7f3d0;
  background: #ecfdf5;
  color: #047857;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
}
.csv-btn:hover { background: #d1fae5; border-color: #6ee7b7; }
.csv-icon { width: 14px; height: 14px; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: all 0.22s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
